'use client';

import { createElement, Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import {
  MARK_LINK,
  MARK_STYLED,
  NODE_HEADING,
  NODE_PARAGRAPH,
  render,
} from 'storyblok-rich-text-react-renderer';

import { useLocale } from '@hooks/useLocale';
import { cn } from '@utils/cn';
import { replaceAllInterpolatedValues } from '@utils/replaceInterpolatedValues';
import { type RichTextStoryblok, type RichtextStoryblok } from '@utils/storyblok/storyblok';
import { ClientStoryblokComponent } from '@common/storyblok/ClientStoryblokComponent';

import { captionVariants } from '../Caption';
import { headingVariants } from '../Heading';
import { LinkMarkResolver } from './LinkMarkResolver';

export type RichTextVariant = 'contained' | 'text';

export type RichTextProps = Omit<RichTextStoryblok, '_uid'> & {
  alignment?: '' | 'left' | 'center' | 'right';
  className?: string;
  color?: 'primary' | 'contrast' | 'tertiary' | 'transparent' | 'inherit';
  content?: RichtextStoryblok;
  variant?: RichTextVariant;
};

const getCustomBlokResolvers = (alignment: RichTextProps['alignment']) => ({
  Image: (props: any) => <ClientStoryblokComponent blok={{ component: 'InlineImage', ...props }} />,
  Button: (props: any) => (
    <ClientStoryblokComponent blok={{ component: 'Button', ...props, size: 'lg' }} />
  ),
  Bullet: (props: any) => (
    <ClientStoryblokComponent blok={{ component: 'Bullet', alignment, ...props }} />
  ),
  KeyValue: (props: any) => <ClientStoryblokComponent blok={{ component: 'KeyValue', ...props }} />,
  MediaLink: (props: any) => (
    <ClientStoryblokComponent blok={{ component: 'MediaLink', ...props }} />
  ),
  LinkList: (props: any) => <ClientStoryblokComponent blok={{ component: 'LinkList', ...props }} />,
  Grid: (props: any) => <ClientStoryblokComponent blok={{ component: 'Grid', ...props }} />,
  Gallery: (props: any) => <ClientStoryblokComponent blok={{ component: 'Gallery', ...props }} />,
  Table: (props: any) => <ClientStoryblokComponent blok={{ component: 'Table', ...props }} />,
});

const getCustomMarkResolvers = ({ localePrefix }: { localePrefix: string }) => ({
  [MARK_LINK]: LinkMarkResolver({ localePrefix }),
  [MARK_STYLED]: (children: React.ReactNode, attrs?: Record<string, any>) => {
    if (attrs?.class === 'caption') {
      return <span className={cn(captionVariants(), 'text-inherit')}>{children}</span>;
    }
    // Fallback: render the class as-is for other styled marks
    return <span className={attrs?.class}>{children}</span>;
  },
});

const getCustomNodeResolvers = () => ({
  [NODE_HEADING]: (children: React.ReactNode, { level }: { level: number }) => {
    const headingClasses = cn(
      'mb-4',
      level === 1 && headingVariants({ variant: 'h1' }),
      level === 2 && headingVariants({ variant: 'h2' }),
      level === 3 && headingVariants({ variant: 'h3' }),
      level === 4 && headingVariants({ variant: 'h4' }),
      level === 5 && headingVariants({ variant: 'h5' }),
    );

    return createElement(`h${level}`, { className: headingClasses }, children);
  },
  [NODE_PARAGRAPH]: (children: React.ReactNode, attrs?: Record<string, any>) => {
    if (attrs?.class === 'caption') {
      return (
        <span className={cn(captionVariants(), 'text-inherit block mb-4 last:mb-0')}>
          {children}
        </span>
      );
    }
    return <p className="font-primary mb-4 last:mb-0">{children}</p>;
  },
});

function RichTextContent(props: RichTextProps) {
  const searchParams = useSearchParams();

  const { alignment, content, color, className, text, values } = props;

  // const { color: sectionBgColor } = useSection();
  const { localePrefix } = useLocale();

  const blokResolvers = getCustomBlokResolvers(alignment);
  const markResolvers = getCustomMarkResolvers({ localePrefix });
  const nodeResolvers = getCustomNodeResolvers();

  const processedText = replaceAllInterpolatedValues(text, values, searchParams) || content;

  if (!processedText?.content?.length) {
    return null;
  }

  return (
    <div
      className={cn(
        'w-full peer',
        'font-primary',
        alignment === 'center' && 'text-center',
        alignment === 'right' && 'text-right',

        color === 'primary' && 'text-text-primary [&*_]:text-text-primary!',
        color === 'contrast' && 'text-text-contrast [&*_]:text-text-contrast!',

        // sectionBgColor === null && '',

        '[&_blockquote]:my-4 [&_blockquote]:pl-4 [&_blockquote]:border-l-4 [&_blockquote]:border-secondary',
        '[&_ul]:pl-5 [&_ul]:list-disc [&_ul]:mb-4 [&_ol]:pl-5 [&_ol]:list-decimal',
        '[&_img]:max-w-full [&_img]:h-auto',
        '[&_a]:text-theme-primary [&_a]:no-underline [&_a:hover]:text-theme-primary [&_a:focus-visible]:no-underline',
        'last:mb-0 first:mt-0',

        className,
      )}
    >
      {render(processedText, {
        nodeResolvers,
        blokResolvers,
        markResolvers,
      })}
    </div>
  );
}

export function RichText(props: RichTextProps) {
  return (
    <Suspense fallback={null}>
      <RichTextContent {...props} />
    </Suspense>
  );
}
